import React, { useState, useRef, useCallback, memo } from 'react';
import { AvatarBadge } from '@/components/AvatarBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Camera, Loader2, Crop, Upload } from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Progress } from '@/components/ui/progress';
import { useQueryClient } from '@tanstack/react-query';
import ReactCrop, { Crop as CropType, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { logError, logDebug } from '@/utils/errorHandler';
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';

interface AvatarProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showUploadButton?: boolean;
  userId?: string | number;
  membershipId?: string | number;
  name?: string;
  avatarUrl?: string;
  onAvatarUpdated?: (newAvatarUrl: string) => void;
}

export function Avatar({
  className,
  size = 'md',
  showUploadButton = false,
  userId,
  membershipId,
  name,
  avatarUrl,
  onAvatarUpdated,
}: AvatarProps) {
  const { user, updateUserContext } = useAuth();
  const queryClient = useQueryClient();
  const { profilePictureModal } = useTeampageTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<'select' | 'crop' | 'upload'>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<CropType>({
    unit: 'px',
    width: 200,
    height: 200,
    x: 50,
    y: 50,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Use user data from auth context if not provided as props
  const displayName = name || user?.name || 'User';
  const displayUserId = userId || user?.id;

  // Fix ID comparison by converting both to strings
  // This handles cases where one ID might be a number and the other a string
  const isCurrentUser =
    user?.id !== undefined &&
    displayUserId !== undefined &&
    String(user.id) === String(displayUserId);

  // For the current user, prefer auth context over props for real-time updates
  const userAvatarUrl = isCurrentUser ? user?.avatar_url || avatarUrl : avatarUrl;

  // Show upload button if specified by parent and this is the current user's avatar
  const shouldShowUploadButton = showUploadButton && isCurrentUser;

  const handleAvatarClick = () => {
    if (shouldShowUploadButton) {
      setIsModalOpen(true);
    }
  };

  const resetModalState = () => {
    setCurrentStep('select');
    setSelectedFile(null);
    setImageSrc('');
    setCompletedCrop(undefined);
    setUploadProgress(0);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetModalState();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Only allow image files
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setSelectedFile(file);

    // Create image preview for cropping
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result?.toString() || '');
      setCurrentStep('crop');
    });
    reader.readAsDataURL(file);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;

    // Calculate a centered square crop that's 80% of the smaller dimension
    const minDimension = Math.min(width, height);
    const cropSize = Math.min(minDimension * 0.8, 300); // Max 300px

    // Center the crop
    const x = (width - cropSize) / 2;
    const y = (height - cropSize) / 2;

    logDebug('Image loaded for cropping', {
      component: 'Avatar',
      action: 'onImageLoad',
      displaySize: { width, height },
      naturalSize: {
        width: e.currentTarget.naturalWidth,
        height: e.currentTarget.naturalHeight,
      },
      cropArea: { x, y, width: cropSize, height: cropSize },
    });

    setCrop({
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x: x,
      y: y,
    });
  }, []);

  // Convert cropped area to canvas and then to File
  const getCroppedFile = useCallback(
    async (crop: PixelCrop): Promise<File | null> => {
      if (!imgRef.current || !crop.width || !crop.height) {
        logError(
          'Missing image reference or invalid crop dimensions',
          {
            component: 'Avatar',
            action: 'getCroppedFile',
            metadata: {
              hasImageRef: !!imgRef.current,
              cropWidth: crop.width,
              cropHeight: crop.height,
            },
          },
          { showToast: false, userFacing: false }
        );
        return null;
      }

      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      // Get the scale ratio between displayed image and natural image
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Calculate the actual crop coordinates on the natural image
      const pixelRatio = window.devicePixelRatio || 1;

      // Set canvas size to the crop size (use the crop dimensions directly)
      canvas.width = crop.width;
      canvas.height = crop.height;

      // Scale the canvas for better quality on high DPI displays
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      canvas.style.width = crop.width + 'px';
      canvas.style.height = crop.height + 'px';

      // Calculate source coordinates (scaled to natural image size)
      const sourceX = crop.x * scaleX;
      const sourceY = crop.y * scaleY;
      const sourceWidth = crop.width * scaleX;
      const sourceHeight = crop.height * scaleY;

      logDebug('Crop details calculated', {
        component: 'Avatar',
        action: 'getCroppedFile',
        displayedImageSize: { width: image.width, height: image.height },
        naturalImageSize: {
          width: image.naturalWidth,
          height: image.naturalHeight,
        },
        scaleX,
        scaleY,
        cropCoords: {
          x: crop.x,
          y: crop.y,
          width: crop.width,
          height: crop.height,
        },
        sourceCoords: {
          x: sourceX,
          y: sourceY,
          width: sourceWidth,
          height: sourceHeight,
        },
      });

      // Draw the cropped portion of the image
      ctx.drawImage(
        image,
        sourceX, // Source x coordinate (on natural image)
        sourceY, // Source y coordinate (on natural image)
        sourceWidth, // Source width (on natural image)
        sourceHeight, // Source height (on natural image)
        0, // Destination x coordinate (on canvas)
        0, // Destination y coordinate (on canvas)
        crop.width / pixelRatio, // Destination width (on canvas)
        crop.height / pixelRatio // Destination height (on canvas)
      );

      return new Promise(resolve => {
        canvas.toBlob(
          blob => {
            if (!blob) {
              logError(
                'Failed to create blob from canvas',
                {
                  component: 'Avatar',
                  action: 'getCroppedFile',
                  metadata: { canvasWidth: canvas.width, canvasHeight: canvas.height },
                },
                { showToast: false, userFacing: false }
              );
              resolve(null);
              return;
            }

            logDebug('Successfully created cropped blob', {
              component: 'Avatar',
              action: 'getCroppedFile',
              size: blob.size,
              type: blob.type,
            });

            const file = new File([blob], selectedFile?.name || 'avatar.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(file);
          },
          'image/jpeg',
          0.9
        );
      });
    },
    [selectedFile]
  );

  const handleCropComplete = async () => {
    if (!completedCrop) {
      toast.error('Please select a crop area');
      return;
    }

    try {
      setCurrentStep('upload');
      const croppedFile = await getCroppedFile(completedCrop);
      if (croppedFile) {
        await uploadAvatar(croppedFile);
      } else {
        toast.error('Failed to process cropped image');
        setCurrentStep('crop');
      }
    } catch (error) {
      logError(
        'Error processing crop',
        {
          component: 'Avatar',
          action: 'handleCropComplete',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error('Failed to process cropped image');
      setCurrentStep('crop');
    }
  };

  const uploadAvatar = async (file: File) => {
    if (!displayUserId && !membershipId) {
      toast.error('User ID or Membership ID not found');
      return;
    }

    // Check if this is the current user
    if (!isCurrentUser) {
      toast.error('You can only update your own avatar');
      return;
    }

    logDebug('Starting avatar upload', {
      component: 'Avatar',
      action: 'uploadAvatar',
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId: displayUserId,
      membershipId: membershipId,
    });

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create a FormData object
      const formData = new FormData();
      formData.append('avatar', file);

      // Use the new dedicated avatar endpoint
      const orgId = user?.organization_id;

      if (!orgId) {
        toast.error('Organization ID is required');
        setIsUploading(false);
        return;
      }

      // Use membership ID if available, otherwise fall back to user ID (for backwards compatibility)
      const endpointId = membershipId || displayUserId;

      // Log endpoint for debugging
      const endpoint = `/api/orgs/${orgId}/memberships/${endpointId}/avatar/`;
      logDebug('Using avatar upload endpoint', {
        component: 'Avatar',
        action: 'uploadAvatar',
        endpoint,
        userId: displayUserId,
        membershipId,
        orgId,
      });

      // Upload with progress tracking
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: progressEvent => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        },
      });

      // Get the avatar URL from the response
      const avatarUrl = response.data?.avatar_url;

      // Update the avatar URL and invalidate cache
      if (avatarUrl) {
        // Add cache-busting timestamp to force browser refresh
        const cacheBustedUrl = `${avatarUrl}?t=${Date.now()}`;

        // Update callback first (for immediate local state)
        if (onAvatarUpdated) {
          onAvatarUpdated(cacheBustedUrl);
        }

        // Update user context for auth-based components
        if (isCurrentUser && user?.id === displayUserId) {
          updateUserContext({ avatar_url: cacheBustedUrl });
        }

        // 1. Invalidate team member queries with current search/filter params
        const currentOrgId = orgId;
        if (currentOrgId) {
          // Invalidate with exact match patterns that are likely active
          queryClient.invalidateQueries({
            predicate: query => {
              const key = query.queryKey;
              return Array.isArray(key) && key[0] === 'teamMembers' && key.includes(currentOrgId);
            },
          });
        }

        // 2. Invalidate all team member queries (fallback)
        queryClient.invalidateQueries({
          queryKey: ['teamMembers'],
          exact: false,
        });

        // 3. Invalidate user product actions
        queryClient.invalidateQueries({
          queryKey: ['userLastProductActions'],
          exact: false,
        });

        // 4. Force immediate refetch of active queries
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: ['teamMembers'],
            exact: false,
            type: 'active', // Only refetch currently active queries
          }),
          queryClient.refetchQueries({
            queryKey: ['userLastProductActions'],
            exact: false,
            type: 'active',
          }),
        ]);

        logDebug('Cache invalidation complete', {
          component: 'Avatar',
          action: 'uploadAvatar',
        });

        // 5. Additional propagation for edge cases
        setTimeout(() => {
          if (onAvatarUpdated) {
            onAvatarUpdated(cacheBustedUrl);
          }
          // Force a context re-render
          if (isCurrentUser && user?.id === displayUserId) {
            updateUserContext({ avatar_url: cacheBustedUrl });
          }
        }, 100);

        logDebug('Avatar updated successfully with cache-busted URL', {
          component: 'Avatar',
          action: 'uploadAvatar',
          cacheBustedUrl,
        });
        toast.success('Avatar updated successfully');
        handleModalClose();
      } else {
        logError(
          'No avatar URL returned from server',
          {
            component: 'Avatar',
            action: 'uploadAvatar',
            metadata: { responseData: response.data },
          },
          { showToast: false, userFacing: false }
        );
        toast.warning('Avatar updated but URL not returned. Refresh may be required.');
        handleModalClose();
      }
    } catch (error: any) {
      // More specific error message based on response
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to upload avatar';

      toast.error(`Failed to upload avatar: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!displayUserId && !membershipId) {
      toast.error('User ID or Membership ID not found');
      return;
    }

    // Check if this is the current user
    if (!isCurrentUser) {
      toast.error('You can only update your own avatar');
      return;
    }

    setIsUploading(true);

    try {
      // Use the new dedicated avatar endpoint
      const orgId = user?.organization_id;

      if (!orgId) {
        toast.error('Organization ID is required');
        setIsUploading(false);
        return;
      }

      // Use membership ID if available, otherwise fall back to user ID (for backwards compatibility)
      const endpointId = membershipId || displayUserId;

      // Log endpoint for debugging
      const endpoint = `/api/orgs/${orgId}/memberships/${endpointId}/avatar/`;
      logDebug('Using endpoint for avatar removal', {
        component: 'Avatar',
        action: 'removeAvatar',
        endpoint,
      });

      // Use the new specific avatar endpoint with DELETE method
      await api.delete(endpoint);

      // Update the avatar URL in the context
      if (onAvatarUpdated) {
        onAvatarUpdated('');
      }

      // Only update user context if this is the current logged-in user's avatar
      if (isCurrentUser && user?.id === displayUserId) {
        updateUserContext({ avatar_url: '' });
      }

      // TARGETED cache invalidation - be surgical, not destructive
      logDebug('Invalidating React Query caches for avatar removal', {
        component: 'Avatar',
        action: 'removeAvatar',
      });

      // 1. Invalidate team member queries with current search/filter params
      const currentOrgId = orgId;
      if (currentOrgId) {
        // Invalidate with exact match patterns that are likely active
        queryClient.invalidateQueries({
          predicate: query => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === 'teamMembers' && key.includes(currentOrgId);
          },
        });
      }

      // 2. Invalidate all team member queries (fallback)
      queryClient.invalidateQueries({
        queryKey: ['teamMembers'],
        exact: false,
      });

      // 3. Invalidate user product actions
      queryClient.invalidateQueries({
        queryKey: ['userLastProductActions'],
        exact: false,
      });

      // 4. Force immediate refetch of active queries
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ['teamMembers'],
          exact: false,
          type: 'active', // Only refetch currently active queries
        }),
        queryClient.refetchQueries({
          queryKey: ['userLastProductActions'],
          exact: false,
          type: 'active',
        }),
      ]);

      // 5. Additional propagation for edge cases
      setTimeout(() => {
        if (onAvatarUpdated) {
          onAvatarUpdated('');
        }
        // Force a context re-render
        if (isCurrentUser && user?.id === displayUserId) {
          updateUserContext({ avatar_url: '' });
        }
      }, 100);

      toast.success('Avatar removed successfully');
      handleModalClose();
    } catch (error: any) {
      logError(
        'Error removing avatar',
        {
          component: 'Avatar',
          action: 'removeAvatar',
          metadata: {
            error,
            errorResponse: error.response?.data,
          },
        },
        { showToast: false, userFacing: false }
      );

      // More specific error message based on response
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to remove avatar';

      toast.error(`Failed to remove avatar: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className={className} onClick={handleAvatarClick}>
        <AvatarBadge src={userAvatarUrl} name={displayName} size={size} />
        {shouldShowUploadButton && (
          <div className="absolute bottom-0 right-0 cursor-pointer rounded-full border-2 border-white bg-primary-500 p-1">
            <Camera className="size-3 text-white" />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {currentStep === 'select' && profilePictureModal.title.select}
              {currentStep === 'crop' && profilePictureModal.title.crop}
              {currentStep === 'upload' && profilePictureModal.title.upload}
            </DialogTitle>
            <DialogDescription>
              {currentStep === 'select' && profilePictureModal.description.select}
              {currentStep === 'crop' && profilePictureModal.description.crop}
              {currentStep === 'upload' && profilePictureModal.description.upload}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center space-y-4 p-4">
            {currentStep === 'select' && (
              <>
                <AvatarBadge src={userAvatarUrl} name={displayName} size="lg" />

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </>
            )}

            {currentStep === 'crop' && imageSrc && (
              <div className="w-full space-y-4">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={c => setCompletedCrop(c)}
                  aspect={1} // Square aspect ratio for avatars
                  minWidth={100}
                  minHeight={100}
                >
                  <img
                    ref={imgRef}
                    alt="Crop"
                    style={{ maxHeight: '400px', maxWidth: '100%' }}
                    src={imageSrc}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
                <div className="text-center text-sm text-muted-foreground">
                  {profilePictureModal.cropInstructions}
                </div>
              </div>
            )}

            {currentStep === 'upload' && (
              <>
                <AvatarBadge src={userAvatarUrl} name={displayName} size="lg" />

                {isUploading && (
                  <div className="w-full space-y-2">
                    <Progress value={uploadProgress} className="h-2 w-full" />
                    <p className="text-center text-xs text-muted-foreground">
                      {profilePictureModal.uploadProgress(uploadProgress)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="sm:justify-between">
            {currentStep === 'select' && (
              <>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={removeAvatar}
                  disabled={isUploading || !userAvatarUrl}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      {profilePictureModal.buttons.removing}
                    </>
                  ) : (
                    profilePictureModal.buttons.removePhoto
                  )}
                </Button>
                <Button type="button" onClick={handleFileSelect} disabled={isUploading}>
                  <Upload className="mr-2 size-4" />
                  {profilePictureModal.buttons.uploadNewPhoto}
                </Button>
              </>
            )}

            {currentStep === 'crop' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCurrentStep('select');
                    setImageSrc('');
                    setSelectedFile(null);
                  }}
                  disabled={isUploading}
                >
                  {profilePictureModal.buttons.back}
                </Button>
                <Button
                  type="button"
                  onClick={handleCropComplete}
                  disabled={isUploading || !completedCrop}
                >
                  <Crop className="mr-2 size-4" />
                  {profilePictureModal.buttons.applyCrop}
                </Button>
              </>
            )}

            {currentStep === 'upload' && (
              <Button
                type="button"
                variant="outline"
                onClick={handleModalClose}
                disabled={isUploading}
              >
                {profilePictureModal.buttons.cancel}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default memo(Avatar);
